import { useSyncExternalStore, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { useTranslation } from '@govard/i18n-core/react';
import { Button, Card, ConfigProvider, Form, Input, InputNumber, Tabs, Timeline, Typography, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { DealChatPanel } from '../../ai-chat/components/ChatPanel.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useWSChannel } from '../../../shared/ws-client/useWSChannel.js';
import { PipelineBadge } from '../components/PipelineBadge.js';
import {
  addDealNote,
  getDealById,
  getDealDetailQueryKey,
  updateDeal,
  type DealActivityItem,
  type DealStageChangedEvent,
} from '../api/deals.client.js';

const { Title, Text } = Typography;

const DEAL_CARD_TABS = ['details', 'activity', 'notes', 'chat'] as const;
type DealCardTab = (typeof DEAL_CARD_TABS)[number];
const MOBILE_CHAT_QUERY = '(max-width: 1023px)';

interface DealDetailsFormValues {
  title?: string;
  amount?: number | null;
  source?: string;
  nextActivityDue?: string;
}

function normalizeDealCardTab(value: string | null): DealCardTab {
  return DEAL_CARD_TABS.includes(value as DealCardTab) ? (value as DealCardTab) : 'details';
}

function formatAmount(amount: number | string, currency: string): string {
  const numeric = typeof amount === 'string' ? Number(amount) : amount;
  const safeAmount = Number.isFinite(numeric) ? numeric : 0;
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(safeAmount);
}

function formatContactName(firstName?: string | null, lastName?: string | null): string {
  return `${firstName ?? ''} ${lastName ?? ''}`.trim();
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function toDatetimeLocal(value?: string | null): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromDatetimeLocal(value?: string): string | null {
  if (value === undefined || value.trim() === '') {
    return null;
  }

  const normalized = value.includes(':') ? value : `${value}T00:00`;
  return new Date(normalized).toISOString();
}

function getActivityBody(activity: DealActivityItem): string | null {
  const body = activity.payload?.body;
  return typeof body === 'string' && body.trim().length > 0 ? body : null;
}

function useIsMobileDealChat(): boolean {
  return useSyncExternalStore(subscribeMobileDealChat, getMobileDealChatSnapshot, getServerMobileDealChatSnapshot);
}

function subscribeMobileDealChat(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }

  const mediaQuery = window.matchMedia(MOBILE_CHAT_QUERY);
  mediaQuery.addEventListener('change', onStoreChange);
  return () => {
    mediaQuery.removeEventListener('change', onStoreChange);
  };
}

function getMobileDealChatSnapshot(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(MOBILE_CHAT_QUERY).matches;
}

function getServerMobileDealChatSnapshot(): boolean {
  return false;
}

function buildWorkspaceSummaryItems(t: ReturnType<typeof useTranslation>['t'], activityCount: number) {
  return [
    { label: t('crm.deal_card.summary.contact'), valueType: 'contact' as const },
    { label: t('crm.deal_card.summary.source'), valueType: 'source' as const },
    { label: t('crm.deal_card.summary.amount'), valueType: 'amount' as const },
    { label: t('crm.deal_card.summary.activity_count'), value: t('crm.deal_card.activity_count', { count: activityCount }) },
    { label: t('crm.deal_card.summary.next_step'), valueType: 'nextActivity' as const },
    { label: t('crm.deal_card.summary.owner'), valueType: 'owner' as const },
  ];
}

export interface DealCardPageProps {
  dealId: string;
}

export function DealCardPage({ dealId }: DealCardPageProps): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = normalizeDealCardTab(searchParams.get('tab'));
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const isMobileChat = useIsMobileDealChat();
  const queryClient = useQueryClient();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const query = useQuery({
    queryKey: getDealDetailQueryKey(workspaceId, dealId),
    queryFn: () => getDealById(dealId),
    enabled: scope !== null,
    retry: false,
  });

  useWSChannel<DealStageChangedEvent>('/ws/deals', {
    enabled: scope !== null,
    onMessage: (event) => {
      if (event.type === 'deal.stage.changed' && event.workspaceId === workspaceId && event.dealId === dealId) {
        void query.refetch();
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: { title: string; amount: number; source: string | null; nextActivityDue: string | null }) =>
      updateDeal(dealId, input),
    onSuccess: async () => {
      const queryKey = getDealDetailQueryKey(workspaceId, dealId);
      await queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
      await queryClient.refetchQueries({ queryKey, type: 'active' });
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: (input: { body: string }) => addDealNote(dealId, input),
    onSuccess: async () => {
      const queryKey = getDealDetailQueryKey(workspaceId, dealId);
      await queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
      await queryClient.refetchQueries({ queryKey, type: 'active' });
    },
  });

  const handleTabChange = (key: string): void => {
    const nextTab = normalizeDealCardTab(key);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTab === 'details') {
      nextSearchParams.delete('tab');
    } else {
      nextSearchParams.set('tab', nextTab);
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  if (query.isLoading) {
    return (
      <main className="mx-auto flex items-center justify-center w-full max-w-5xl p-6" style={{ height: '80vh' }}>
        <Spin size="large" />
        <Text style={{ marginLeft: 16 }}>{t('crm.deal_card.loading')}</Text>
      </main>
    );
  }

  if (query.isError || query.data === undefined) {
    return (
      <main className="mx-auto flex w-full max-w-4xl p-6 items-center justify-center" style={{ height: '80vh' }}>
        <Card style={{ textAlign: 'center', maxWidth: 480, width: '100%' }}>
          <Title level={4}>{t('crm.deal_card.error_title')}</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {query.error instanceof Error ? query.error.message : t('crm.deal_card.error_retry')}
          </Text>
          <Button type="primary" onClick={() => void query.refetch()}>{t('crm.deal_card.retry')}</Button>
        </Card>
      </main>
    );
  }

  const deal = query.data;
  const contactName = formatContactName(deal.contact?.firstName, deal.contact?.lastName);
  const safeContactName = contactName || t('crm.deal_card.details.contact_missing');
  const activityCount = deal.activities.length;
  const workspaceSummaryItems = buildWorkspaceSummaryItems(t, activityCount);
  const noteActivities = deal.activities.filter((activity) => activity.kind === 'note_added');

  const handleDetailsSave = (values: DealDetailsFormValues): void => {
    updateMutation.mutate({
      title: values.title?.trim() ?? deal.title,
      amount: values.amount ?? Number(deal.amount),
      source: values.source?.trim() ? values.source.trim() : null,
      nextActivityDue: fromDatetimeLocal(values.nextActivityDue),
    });
  };

  const handleNoteSave = (values: { body?: string }): void => {
    const body = values.body?.trim();
    if (!body) {
      return;
    }
    addNoteMutation.mutate({ body });
  };

  const tabItems = [
    {
      key: 'details',
      label: t('crm.deal_card.tabs.details'),
      children: (
        <div className="flex flex-col h-full bg-white">
          <div className="border-b border-gray-200 px-5 py-3 flex-none">
            <span className="text-xs font-semibold tracking-wider text-gray-900 uppercase">{t('crm.deal_card.details_title')}</span>
          </div>
          <div className="px-5 py-5 flex-1 overflow-y-auto">
            <Form
              key={`${deal.id}:${deal.updatedAt ?? ''}:details`}
              layout="vertical"
              initialValues={{
                title: deal.title,
                amount: Number(deal.amount),
                source: deal.source ?? '',
                nextActivityDue: toDatetimeLocal(deal.nextActivityDue),
              }}
              onFinish={handleDetailsSave}
              className="flex flex-col gap-1"
            >
              <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                <Form.Item 
                  label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">{t('crm.deal_card.form.title')}</span>}
                  name="title" 
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input size="small" variant="borderless" className="px-0 py-0 text-sm text-gray-900 font-medium focus:bg-white focus:ring-1 focus:ring-slate-900 transition-all rounded-sm" />
                </Form.Item>
              </div>
              
              <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                <Form.Item 
                  label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">{t('crm.deal_card.form.amount')}</span>}
                  name="amount" 
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber size="small" variant="borderless" className="w-full px-0 py-0 text-sm text-gray-900 font-medium focus:bg-white focus:ring-1 focus:ring-slate-900 transition-all rounded-sm" min={0} />
                </Form.Item>
              </div>

              <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                <Form.Item 
                  label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">{t('crm.deal_card.form.source')}</span>}
                  name="source"
                  style={{ marginBottom: 0 }}
                >
                  <Input size="small" variant="borderless" className="px-0 py-0 text-sm text-gray-900 font-medium focus:bg-white focus:ring-1 focus:ring-slate-900 transition-all rounded-sm" placeholder="Не указан" />
                </Form.Item>
              </div>

              <div className="group px-2 py-1.5 -mx-2 rounded hover:bg-gray-50 transition-colors">
                <Form.Item 
                  label={<span className="text-xs font-medium text-gray-500 uppercase tracking-widest">{t('crm.deal_card.form.next_activity')}</span>}
                  name="nextActivityDue"
                  style={{ marginBottom: 0 }}
                >
                  <Input size="small" type="datetime-local" variant="borderless" className="px-0 py-0 text-sm text-gray-900 font-medium focus:bg-white focus:ring-1 focus:ring-slate-900 transition-all rounded-sm" />
                </Form.Item>
              </div>
              <Button type="primary" size="small" style={{ marginTop: 16 }} htmlType="submit">
                {t('crm.deal_card.details_save')}
              </Button>
            </Form>
            {updateMutation.isPending && (
              <div className="mt-4 text-xs text-gray-400 flex items-center gap-2">
                 <Spin size="small" /> Сохранение...
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'activity',
      label: t('crm.deal_card.tabs.activity'),
      children: (
        <div className="px-6 py-5">
          <div className="text-xs font-semibold tracking-wider text-gray-900 uppercase mb-6">{t('crm.deal_card.activity_title')}</div>
          <Timeline
            items={deal.activities.map((activity, idx) => ({
              color: idx === 0 ? '#0f172a' : 'gray',
              children: (
                <div className="rounded border border-gray-200 bg-white px-4 py-3" style={{ marginBottom: 8, boxShadow: 'none' }}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <span className="block text-sm font-medium text-gray-900">{activity.subject ?? activity.kind}</span>
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">{activity.kind}</span>
                  </div>
                  <span className="block text-xs text-gray-500 mt-2">
                    {formatDateTime(activity.occurredAt ?? activity.createdAt)} · {activity.actor}
                  </span>
                </div>
              ),
            }))}
          />
        </div>
      ),
    },
    {
      key: 'notes',
      label: t('crm.deal_card.tabs.notes'),
      children: (
        <div className="px-6 py-5">
          <div className="flex flex-wrap items-center justify-between gap-3" style={{ marginBottom: 18 }}>
            <Title level={5} style={{ margin: 0 }}>{t('crm.deal_card.notes_title')}</Title>
          </div>
          <Form key={`${deal.id}:${deal.updatedAt ?? ''}:note`} layout="vertical" onFinish={handleNoteSave}>
            <Form.Item label={t('crm.deal_card.note_new_label')} name="body">
              <Input.TextArea rows={4} placeholder={t('crm.deal_card.note_placeholder')} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={addNoteMutation.isPending}>
              {t('crm.deal_card.note_save')}
            </Button>
          </Form>
          <div className="flex flex-col gap-3" style={{ marginTop: 24 }}>
          {noteActivities.length > 0 ? (
            noteActivities.map((activity) => (
              <div key={activity.id} className="border border-gray-200 rounded-md bg-gray-50 px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <Text strong className="text-sm text-gray-900">{activity.subject ?? t('crm.deal_card.note_subject_fallback')}</Text>
                  <Text type="secondary" className="text-xs">
                    {formatDateTime(activity.createdAt)} · {activity.actor}
                  </Text>
                </div>
                <Text className="text-sm whitespace-pre-wrap text-gray-800">{getActivityBody(activity) ?? activity.subject ?? ''}</Text>
              </div>
            ))
          ) : deal.notes && deal.notes.trim().length > 0 ? (
            <div className="border border-gray-200 rounded-md bg-gray-50 px-4 py-3">
               <Text className="text-sm whitespace-pre-wrap text-gray-800">{deal.notes}</Text>
            </div>
          ) : (
            <Text type="secondary">{t('crm.deal_card.notes_empty')}</Text>
          )}
          </div>
        </div>
      ),
    },
  ];

  tabItems.push({
    key: 'chat',
    label: t('crm.deal_card.tabs.chat'),
    children: isMobileChat ? <></> : (
        <div className="px-6 py-5">
          <Title level={5} style={{ marginBottom: 16 }}>{t('crm.deal_card.chat_title')}</Title>
          <Text type="secondary">{t('crm.deal_card.chat_desktop_note')}</Text>
        </div>
    ),
  });

  return (
    <ConfigProvider theme={{ token: { borderRadius: 6, colorPrimary: '#0f172a', colorBorder: '#e5e7eb', colorBgLayout: '#f5f5f4' } }}>
    <main className="flex h-full w-full flex-col overflow-hidden bg-stone-50">
      <header data-testid="deal-card-header" className="bg-white flex-none px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link
              to="/deals"
              aria-label={t('crm.deal_card.back_to_deals')}
              className="mb-2 inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-gray-500 uppercase hover:text-gray-900"
            >
              <ArrowLeftOutlined />
              {t('crm.deal_card.back_to_deals')}
            </Link>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <h1 className="text-xl font-semibold text-gray-900 m-0">{deal.title}</h1>
              <PipelineBadge stage={deal.stage} />
              <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">{safeContactName}</span>
              {deal.source ? (
                <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-500">{deal.source}</span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => handleTabChange('notes')}>
              {t('crm.deal_card.quick_add_note')}
            </Button>
            <Button type="primary">
              Сгенерировать follow-up
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-none bg-white border-y border-gray-200 flex flex-wrap items-center px-2">
        {workspaceSummaryItems.map((item) => {
          let value: string;
          if (item.valueType === 'amount') {
            value = formatAmount(deal.amount, deal.currency);
          } else if (item.valueType === 'nextActivity') {
            value = deal.nextActivityDue ? formatDateTime(deal.nextActivityDue) : t('crm.deal_card.details.next_activity_missing');
          } else if (item.valueType === 'owner') {
            value = deal.owner?.name ?? deal.owner?.email ?? t('crm.deal_card.owner_missing');
          } else if (item.valueType === 'contact') {
            value = safeContactName;
          } else if (item.valueType === 'source') {
            value = deal.source ?? t('crm.deal_card.details.source_missing');
          } else {
            value = item.value ?? '';
          }

          return (
            <div key={item.label} className="px-4 py-3 border-r border-transparent hover:bg-gray-50 transition-colors min-w-36">
              <span className="block text-xs tracking-widest uppercase text-gray-500 font-medium mb-0.5">
                {item.label}
              </span>
              <span className="block text-sm font-medium text-gray-900">
                {value}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="flex-none border-r border-gray-200 bg-white overflow-y-auto" style={{ width: 320 }}>
          {tabItems.find((t) => t.key === 'details')?.children}
        </aside>

        <section className="flex-1 min-w-0 bg-white overflow-y-auto px-6 py-5">
           <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={tabItems.map((item) => item.key === 'details' ? { ...item, children: null } : item)}
           />
        </section>

        {!isMobileChat ? (
          <aside className="flex-none border-l border-gray-200 bg-white flex flex-col min-h-0" style={{ width: 340 }}>
            <div className="border-b border-gray-200 px-5 py-3 flex-none flex items-center justify-between">
              <span className="text-xs font-semibold tracking-wider text-gray-900 uppercase">AI Chat</span>
              <span className="text-xs uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-bold">Готов</span>
            </div>
            <div className="flex-1 min-h-0 relative overflow-hidden">
              <DealChatPanel scope={scope} dealId={dealId} />
            </div>
          </aside>
        ) : null}
      </div>

      {isMobileChat && activeTab === 'chat' ? (
        <div 
          data-testid="deal-chat-mobile-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deal-chat-mobile-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleTabChange('details');
          }}
        >
          <div 
            className="flex flex-col w-full bg-white rounded-2xl overflow-hidden shadow-2xl border border-gray-200"
            style={{ height: '85vh' }}
          >
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center justify-between">
              <Title id="deal-chat-mobile-title" level={5} style={{ margin: 0, fontWeight: 600 }}>
                {t('crm.deal_card.chat_mobile_title')}
              </Title>
              <span className="text-xs uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded font-bold">Готов</span>
            </div>
            <div className="flex-1 overflow-hidden relative">
              <DealChatPanel scope={scope} dealId={dealId} />
            </div>
            <div className="px-4 py-4 border-t border-gray-100 flex justify-end bg-gray-50">
              <Button type="primary" onClick={() => handleTabChange('details')}>
                {t('crm.deal_card.close_chat')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
    </ConfigProvider>
  );
}
